import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Maximize2, Palette } from 'lucide-react';
import { getPrototypeDesign, type PrototypeDto } from '@aiai/shared';
import { setPrototypeDesign } from '../api';
import { qk } from '../api/queryKeys';
import { invalidatePrototype } from '../api/prototypeCache';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { DesignPicker, DesignSwatch } from './DesignPicker';
import { DesignPreviewOverlay } from './DesignPreviewApp';
import { SectionCard } from './SectionCard';
import { Spinner } from './Spinner';

/**
 * Shows the prototype's current design style and lets the owner switch it.
 * Switching files a restyle ticket (proposed — the PM plans it and the
 * project's autonomy dial governs release) and remembers the new choice.
 */
export function PrototypeDesignCard({ prototype }: { prototype: PrototypeDto }) {
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filedDesign, setFiledDesign] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const design = prototype.designId ? getPrototypeDesign(prototype.designId) : undefined;

  const mutation = useMutation({
    mutationFn: (designId: string) => setPrototypeDesign(prototype.id, designId),
    onSuccess: (updated) => {
      setFiledDesign(updated.designId);
      invalidatePrototype(queryClient, prototype.id);
      // The restyle lands as a new issue on this project's board.
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(prototype.id) });
    },
  });

  return (
    <SectionCard title="Design style">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {design ? (
              <div className="w-20 flex-none overflow-hidden rounded-[6px] border border-line">
                <DesignSwatch design={design} compact />
              </div>
            ) : (
              <div className="flex h-11 w-20 flex-none items-center justify-center rounded-[6px] border border-dashed border-line">
                <Palette className="h-4 w-4 text-ink-faint" aria-hidden="true" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">{design?.name ?? 'Default'}</p>
              <p className="truncate text-xs text-ink-faint">
                {design?.tagline ?? 'The scaffold’s clean, light default look.'}
              </p>
            </div>
          </div>
          <div className="flex flex-none gap-2">
            {design && (
              <button type="button" className="btn-secondary" onClick={() => setPreviewOpen(true)}>
                <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
                Preview
              </button>
            )}
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPickerOpen(true)}
              disabled={mutation.isPending}
            >
              {mutation.isPending && <Spinner className="h-3.5 w-3.5" />}
              Change style
            </button>
          </div>
        </div>

        <p className="text-xs text-ink-faint">
          Switching styles files a restyle ticket for the team — the Product Manager plans
          it and it is built like any other issue.
        </p>

        {mutation.isSuccess && filedDesign && (
          <Callout tone="success">
            Restyle ticket filed for “{getPrototypeDesign(filedDesign)?.name ?? filedDesign}” —
            find it on the issue board below.
          </Callout>
        )}

        {mutation.isError && (
          <Callout tone="danger">
            {errorMessage(mutation.error, 'Could not change the design style')}
          </Callout>
        )}
      </div>

      {previewOpen && design && (
        <DesignPreviewOverlay
          designs={[design]}
          designId={design.id}
          onNavigate={() => {}}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      <DesignPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={prototype.designId}
        onSelect={(id) => {
          if (id && id !== prototype.designId) mutation.mutate(id);
        }}
        allowNone={false}
        confirmLabel="File restyle ticket"
      />
    </SectionCard>
  );
}
